/* Applying a move and resolving the magnetic chain reaction (D4, D6, D7). */
import { DIR8, edgeToward, poleToward } from './shapes.ts';
import { bitsOf, bodyOf, bond, cellOf, clonePosition, inb, labelBodies, removePiece } from './position.ts';
import type { Position } from './position.ts';
import { MAX_PIECES, MAX_WAVES, other } from './types.ts';
import type { Event, Move, ShapeId, Side } from './types.ts';

export interface Applied { pos: Position; events: Event[] }

/**
 * Contact between piece a, looking in board direction (dr,dc), and piece b looking back.
 *  1 = same poles (a pushes b), -1 = opposite poles (they attract), 0 = no magnetic contact.
 * D7, one gate everywhere: a contact counts only if at least one side presents a true edge; poles then come from the tables,
 * and a neutral corner never interacts. The same function is used for actor contacts and for push collisions.
 */
export function contact(p: Position, a: number, dr: number, dc: number, b: number): 1 | -1 | 0 {
  const sa = p.owner[a] as Side, sb = p.owner[b] as Side, ta = p.shape[a] as ShapeId, tb = p.shape[b] as ShapeId;
  if (!edgeToward(ta, sa, dr, dc) && !edgeToward(tb, sb, -dr, -dc)) return 0;
  const pa = poleToward(ta, sa, dr, dc), pb = poleToward(tb, sb, -dr, -dc);
  if (!pa || !pb) return 0;
  return pa === pb ? 1 : -1;
}

/** Apply a move to a position. Pure: returns the new position and the events that explain it. Assumes the move is legal. */
export function applyMove(p0: Position, mv: Move): Applied {
  const p = clonePosition(p0);
  const events: Event[] = [];
  const i = mv.piece;
  if (!p.alive[i]) throw new Error('piece is not on the board');
  const body = mv.solo ? [i] : bodyOf(p, i);
  if (mv.solo) {
    const partners = bitsOf(p.bonds[i]);
    if (partners.length) {
      for (const j of partners) p.bonds[j] &= ~(1 << i);
      p.bonds[i] = 0;
      events.push({ kind: 'unbond', piece: i, partners });
    }
  }
  const from = cellOf(p.row[i], p.col[i]);
  for (const m of body) p.board[cellOf(p.row[m], p.col[m])] = -1;
  const actors: number[] = [], dragged: number[] = [];
  for (const m of body) {
    const r = p.row[m] + mv.dr * mv.dist, c = p.col[m] + mv.dc * mv.dist;
    if (inb(r, c)) { p.row[m] = r; p.col[m] = c; p.board[cellOf(r, c)] = m; actors.push(m); }
    else dragged.push(m);
  }
  events.push({ kind: 'move', piece: i, from, to: cellOf(p.row[i], p.col[i]), dist: mv.dist, solo: mv.solo, body: body.slice() });
  for (const m of dragged) { p.row[m] = -1; p.col[m] = -1; removePiece(p, m); events.push({ kind: 'drag-off', piece: m, by: i }); }
  resolveWaves(p, actors, events);
  p.toMove = other(p.toMove);
  return { pos: p, events };
}

type Vec = [number, number];
const codeOf = (v: Vec): number => (v[0] + 1) * 3 + (v[1] + 1);
const vecOfCode = (code: number): Vec => [Math.floor(code / 3) - 1, (code % 3) - 1];

/**
 * Wave resolution. Each wave is read entirely from the board as it stands at the start of the wave (D6):
 *  1. every actor (a piece that moved in the previous wave) makes magnetic contact with its neighbours;
 *     same poles push the neighbour's body, opposite poles are a bond candidate; two actors repel each other mutually,
 *     a resting piece never pushes an actor;
 *  2. forces on a body are summed to one direction (or cancel);
 *  3. a moving body shoves a body in front of it along (same-pole contact) if that body has no magnetic force of its own;
 *     a body shoved from two directions jams. A body stays still if any cell it needs is occupied by something not moving
 *     the same way, if two bodies want the same cell, or if something it shoves stays still. This is computed as a greatest
 *     fixpoint (bodies are only ever removed from the moving set), so the result is independent of piece numbering;
 *  4. all moves happen at once; pieces leaving the board are lost;
 *  5. bonds form between candidate pairs that end the wave adjacent with opposite poles (D4);
 *  6. everything that moved is an actor in the next wave. Resolution ends when nothing moves.
 */
export function resolveWaves(p: Position, actors: number[], ev: Event[]): void {
  let wave = 0;
  while (actors.length) {
    wave++;
    if (wave > MAX_WAVES) { ev.push({ kind: 'cutoff', wave }); return; }
    ev.push({ kind: 'wave', wave });
    const label = labelBodies(p);
    const members = new Map<number, number[]>();
    for (let k = 0; k < MAX_PIECES; k++) {
      if (!p.alive[k]) continue;
      const b = label[k]; let arr = members.get(b); if (!arr) { arr = []; members.set(b, arr); } arr.push(k);
    }
    const isActor = new Uint8Array(MAX_PIECES);
    for (const a of actors) isActor[a] = 1;

    // 1. magnetic contacts
    const fr = new Map<number, number>(), fc = new Map<number, number>();
    const addForce = (b: number, dr: number, dc: number): void => { fr.set(b, (fr.get(b) ?? 0) + dr); fc.set(b, (fc.get(b) ?? 0) + dc); };
    const attract: Array<[number, number]> = [];
    const seen = new Set<number>();
    for (const f of actors) {
      if (!p.alive[f]) continue;
      for (const [dr, dc] of DIR8) {
        const rr = p.row[f] + dr, cc = p.col[f] + dc;
        if (!inb(rr, cc)) continue;
        const n = p.board[cellOf(rr, cc)];
        if (n < 0 || label[n] === label[f]) continue;
        const key = f < n ? f * MAX_PIECES + n : n * MAX_PIECES + f;
        if (seen.has(key)) continue;
        seen.add(key);
        const c = contact(p, f, dr, dc, n);
        if (c !== 0) ev.push({ kind: 'contact', wave, a: f, b: n, dr, dc, effect: c === 1 ? 'push' : 'attract' });
        if (c === 1) { addForce(label[n], dr, dc); if (isActor[n]) addForce(label[f], -dr, -dc); }
        else if (c === -1) attract.push([f, n]);
      }
    }

    // 2. net direction per body
    const own = new Map<number, Vec>();
    for (const [b, r] of fr) {
      const dr = Math.sign(r), dc = Math.sign(fc.get(b) ?? 0);
      if (dr || dc) own.set(b, [dr, dc]); else ev.push({ kind: 'cancel', wave, body: members.get(b)!.slice() });
    }

    // 3. greatest fixpoint over the set of moving bodies
    const inert = new Set<number>();
    const why = new Map<number, { reason: 'blocked' | 'contested' | 'jammed'; v: Vec }>();
    let moving = new Map<number, Vec>();
    let collisionAttract: Array<[number, number, number, number]> = [];
    for (;;) {
      const chained = new Map<number, Set<number>>();
      const rebuild = (): void => {
        moving = new Map();
        for (const [b, v] of own) if (!inert.has(b)) moving.set(b, v);
        for (const [b, set] of chained) if (set.size === 1 && !inert.has(b)) moving.set(b, vecOfCode(set.values().next().value as number));
      };
      rebuild();
      for (;;) {
        let changed = false;
        for (const [b, v] of [...moving]) {
          for (const m of members.get(b)!) {
            const rr = p.row[m] + v[0], cc = p.col[m] + v[1];
            if (!inb(rr, cc)) continue;
            const q = p.board[cellOf(rr, cc)];
            if (q < 0 || label[q] === b) continue;
            const cb = label[q];
            if (own.has(cb) || inert.has(cb)) continue;              // a magnetically driven body is not deflected by a shove
            if (contact(p, m, v[0], v[1], q) !== 1) continue;        // only same poles transmit a shove
            let set = chained.get(cb); if (!set) { set = new Set(); chained.set(cb, set); }
            const code = codeOf(v);
            if (!set.has(code)) { set.add(code); changed = true; }
          }
        }
        if (!changed) break;
        rebuild();
      }
      const newInert: number[] = [];
      for (const [b, set] of chained) if (set.size > 1 && !inert.has(b)) { newInert.push(b); why.set(b, { reason: 'jammed', v: [0, 0] }); }
      collisionAttract = [];
      const targets = new Map<number, Set<number>>();
      for (const [b, v] of moving) {
        for (const m of members.get(b)!) {
          const rr = p.row[m] + v[0], cc = p.col[m] + v[1];
          if (!inb(rr, cc)) continue;                                 // falls off the board: allowed
          const cell = cellOf(rr, cc), q = p.board[cell];
          if (q < 0) { let s = targets.get(cell); if (!s) { s = new Set(); targets.set(cell, s); } s.add(b); continue; }
          if (label[q] === b) continue;
          const c = contact(p, m, v[0], v[1], q);
          const mv2 = moving.get(label[q]);
          const follows = mv2 !== undefined && mv2[0] === v[0] && mv2[1] === v[1] && c === 1;
          if (!follows) { newInert.push(b); why.set(b, { reason: 'blocked', v }); if (c === -1) collisionAttract.push([m, q, v[0], v[1]]); }
        }
      }
      for (const s of targets.values()) if (s.size > 1) for (const b of s) { newInert.push(b); why.set(b, { reason: 'contested', v: moving.get(b)! }); }
      if (!newInert.length) break;
      for (const b of newInert) inert.add(b);
    }
    for (const [b, info] of why) if (inert.has(b)) ev.push({ kind: 'blocked', wave, body: members.get(b)!.slice(), dr: info.v[0], dc: info.v[1], reason: info.reason });
    for (const [m, q, dr, dc] of collisionAttract) ev.push({ kind: 'contact', wave, a: m, b: q, dr, dc, effect: 'attract' });

    // 4. all moves at once
    const movedPieces: number[] = [];
    if (moving.size) {
      const fromCell = new Int16Array(MAX_PIECES).fill(-1), toCell = new Int16Array(MAX_PIECES).fill(-1);
      const fallen: number[] = [];
      for (const [b, v] of moving) for (const m of members.get(b)!) {
        fromCell[m] = cellOf(p.row[m], p.col[m]); p.board[fromCell[m]] = -1;
        const r = p.row[m] + v[0], c = p.col[m] + v[1];
        if (inb(r, c)) toCell[m] = cellOf(r, c); else fallen.push(m);
        p.row[m] = r; p.col[m] = c;
      }
      for (const [b, v] of moving) {
        const mem = members.get(b)!;
        for (const m of mem) if (toCell[m] >= 0) { p.board[toCell[m]] = m; movedPieces.push(m); }
        ev.push({ kind: 'push', wave, body: mem.slice(), dr: v[0], dc: v[1], from: mem.map(m => fromCell[m]), to: mem.map(m => toCell[m]) });
      }
      for (const m of fallen) { p.row[m] = -1; p.col[m] = -1; removePiece(p, m); ev.push({ kind: 'pushed-off', wave, piece: m }); }
    }

    // 5. bonds
    const bonded = new Set<number>();
    for (const [a, b] of [...attract, ...collisionAttract.map(([m, q]) => [m, q] as [number, number])]) {
      if (!p.alive[a] || !p.alive[b]) continue;
      const dr = p.row[b] - p.row[a], dc = p.col[b] - p.col[a];
      if (Math.abs(dr) > 1 || Math.abs(dc) > 1 || (dr === 0 && dc === 0)) continue;
      if (contact(p, a, dr, dc, b) !== -1) continue;
      if (p.bonds[a] & (1 << b)) continue;
      const key = a < b ? a * MAX_PIECES + b : b * MAX_PIECES + a;
      if (bonded.has(key)) continue;
      bonded.add(key);
      bond(p, a, b);
      ev.push({ kind: 'bond', wave, a, b });
    }
    actors = movedPieces;
  }
}
